import {
  Form,
  required,
  SelectInput,
  useCreate,
  useNotify,
  useRefresh,
} from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';

import { usePetPlans } from '@pumpkincare/users';

import { cancellationReasons } from '../../../constants/cancellationReasons';
import { parseAndFormatDate, STATUSES } from '../../../shared/utils';
import { cancelPendingPolicy } from '../../customers/service/customerService';
import { petCancelModalStyle } from './pet-cancel-modal-style';

function PetCancelModal(props) {
  const { onClose, isOpen, policy_plan = {} } = props;
  const classes = petCancelModalStyle();
  const [create, { isLoading }] = useCreate();
  const refresh = useRefresh();
  const notify = useNotify();
  const { fer6664CancelPendingPolicies } = useFlags();

  const { data: petPlanData = [] } = usePetPlans(policy_plan.pet_id);

  const petPlan = petPlanData.find(plan => plan.status === 'renew_pending');

  const isCancellingRenewPendingPolicy = !!policy_plan.policy_end_date;
  const endDate = policy_plan.policy_effective_date || petPlan?.plan_effective_date;

  function handleCancellation({ cancellation_reason }) {
    if (fer6664CancelPendingPolicies && policy_plan.status === STATUSES.PENDING) {
      cancelPendingPolicy({ policyId: policy_plan.id, petId: policy_plan.pet_id })
        .then(() => {
          notify(`The pending policy has been successfully cancelled.`, {
            type: 'success',
          });
          onClose();
          refresh();
        })
        .catch(error => {
          notify(
            `There was an error while cancelling the pending policy: ${error.message}`,
            {
              type: 'warning',
            }
          );
        });
    } else {
      create(
        `cancellations/renewals`,
        {
          data: {
            reason: cancellation_reason,
            pet_id: policy_plan.pet_id,
            type: isCancellingRenewPendingPolicy ? 'policy' : 'plan',
          },
        },
        {
          onSuccess: () => {
            onClose();
            refresh();
          },
          onError: error => {
            notify(
              `There was an error while cancelling the renewal plan/policy: ${error.message}`,
              {
                type: 'warning',
              }
            );
          },
        }
      );
    }
  }

  const renderCancellationText = () => {
    if (fer6664CancelPendingPolicies && policy_plan.status === STATUSES.PENDING) {
      return `This policy will be deactivated. This pet will no longer have coverage 
      as of this policy’s effective date. All pet plans will be canceled as well, 
      and all lapsed invoices will be labeled as uncollectible. This will not cancel associated wellness plans.`;
    }
    return `This ${
      isCancellingRenewPendingPolicy ? 'policy' : 'plan'
    }  will not be activated. 
    This pet will no longer have coverage as of ${parseAndFormatDate(endDate)}'`;
  };

  return (
    <Dialog maxWidth='md' open={isOpen} onClose={onClose}>
      <Form onSubmit={handleCancellation}>
        <DialogTitle>
          <Typography className={classes.modalTitle}>
            {fer6664CancelPendingPolicies && policy_plan.status === STATUSES.PENDING
              ? 'Confirm Cancellation'
              : 'Confirm Non-renewal'}
          </Typography>
          <Typography className={classes.modalSubTitle}>
            {renderCancellationText()}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <div className={classes.informationDiv}>
            <Typography className={classes.information}>PET:</Typography>
            <Typography className={classes.information}>
              {policy_plan.pet_name}
            </Typography>
          </div>

          {policy_plan.policy_effective_date ? (
            <div className={classes.informationDiv}>
              <Typography className={classes.information}>POLICY PERIOD:</Typography>
              <Typography className={classes.information}>
                {`${parseAndFormatDate(
                  policy_plan.policy_effective_date
                )} - ${parseAndFormatDate(policy_plan.policy_end_date)}`}
              </Typography>
            </div>
          ) : null}
          {petPlan ? (
            <div className={classes.informationDiv}>
              <Typography className={classes.information}>
                PREVENTIVE ESSENTIALS PLAN PERIOD:
              </Typography>
              <Typography className={classes.information}>
                {`${parseAndFormatDate(
                  petPlan.plan_effective_date
                )} - ${parseAndFormatDate(petPlan.plan_end_date)}`}
              </Typography>
            </div>
          ) : null}

          {policy_plan.status === STATUSES.RENEW_PENDING ? (
            <SelectInput
              fullWidth
              validate={required()}
              source='cancellation_reason'
              choices={cancellationReasons}
              label='Cancellation Reason'
            />
          ) : null}
        </DialogContent>
        <DialogActions style={{ justifyContent: 'space-between' }}>
          <Button
            disabled={isLoading}
            onClick={onClose}
            className={classes.cancelButton}
          >
            CANCEL
          </Button>
          <Button
            disabled={isLoading}
            type='submit'
            className={classes.updateButton}
          >
            CONFIRM
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
}

PetCancelModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  policy_plan: PropTypes.object,
};

export default PetCancelModal;
